import { useState, useEffect, ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Search, User, ChevronDown, LayoutDashboard, Zap, Sparkles, ShoppingCart, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCategories, fallbackCategories } from "@/hooks/useCategories";
import { CartDrawer } from "@/components/cart/CartDrawer";
import { SearchDialog } from "@/components/search/SearchDialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useAdmin } from "@/hooks/useAdmin";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { getOptimizedImageUrl } from "@/lib/image-optimization";

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [hasActiveFlashSale, setHasActiveFlashSale] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdmin();
  const { data: dbCategories } = useCategories();
  const location = useLocation();
  
  const isHomePage = location.pathname === "/";
  const isAboutPage = location.pathname === "/about";
  const isHeroPage = isHomePage || isAboutPage; // Pages with full-bleed hero images
  
  // Logic to determine which categories to show in header
  // 1. If any are marked 'is_featured', use those (limit 3)
  // 2. Otherwise fallback to first 3
  const availableCategories = dbCategories && dbCategories.length > 0 ? dbCategories : fallbackCategories;
  const featuredCategories = availableCategories.filter(c => c.is_featured);
  const displayCategories = featuredCategories.length > 0 
    ? featuredCategories.slice(0, 3) 
    : availableCategories.slice(0, 3);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const checkFlashSales = async () => {
      const now = new Date().toISOString();
      const { data } = await supabase
        .from('flash_sales')
        .select('id')
        .eq('is_active', true)
        .lte('start_time', now)
        .gte('end_time', now)
        .limit(1);
      setHasActiveFlashSale((data?.length || 0) > 0);
    };
    checkFlashSales();
  }, []);

  return (
    <header 
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-700 ease-elegant",
        (scrolled || !isHeroPage)
          ? "bg-white/80 backdrop-blur-lg border-b border-gold/5 shadow-sm py-0" 
          : "bg-transparent backdrop-blur-none py-4"
      )}
    >
      {/* Top Bar - Subtle & Elegant */}
      <div className={cn(
        "transition-all duration-500 overflow-hidden",
        (scrolled || isHeroPage) ? "h-0 opacity-0" : "h-10 opacity-100"
      )}>
        <div className="bg-charcoal text-white/90 text-center py-2 text-xs uppercase tracking-[0.25em] font-bold relative">
           <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gold/10 to-transparent animate-shimmer" style={{ backgroundSize: '1000% 100%' }} />
           <span className="relative z-10">
             Complimentary Curated Global Shipping · Leestone Heritage
           </span>
        </div>
      </div>

      <div className="container-luxury">
        <div className="relative flex items-center justify-between h-20 lg:h-24">
          
          {/* Left: Mobile Trigger & Left Nav */}
          <div className="flex items-center gap-8 flex-1">
            <button
              className={cn(
                "lg:hidden p-2 transition-colors hover:scale-110 duration-300",
                scrolled ? "text-charcoal hover:text-gold" : (isHeroPage ? "text-white hover:text-gold" : "text-charcoal hover:text-gold")
              )}
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open Menu"
            >
              <Menu size={20} />
            </button>

            <nav className="hidden lg:flex items-center gap-10">
              {displayCategories.map((category) => (
                <div
                  key={category.id}
                  className="group"
                  onMouseEnter={() => setActiveCategory(category.slug)}
                  onMouseLeave={() => setActiveCategory(null)}
                >
                  <Link
                    to={`/shop?category=${category.slug}`}
                    className={cn(
                      "relative text-sm tracking-wide font-medium transition-all duration-300 py-8 inline-flex items-center gap-2",
                      scrolled ? "text-charcoal" : (isHeroPage ? "text-white" : "text-charcoal"),
                      "hover:text-gold"
                    )}
                  >
                    {category.name}
                    <div className="absolute bottom-6 left-0 w-0 h-[1px] bg-gold transition-all duration-500 group-hover:w-full" />
                  </Link>

                  {/* Redesigned Mega Menu */}
                  <div
                    className={cn(
                      "fixed left-0 right-0 bg-white border-b border-gold/5 shadow-2xl transition-all duration-700 ease-elegant overflow-hidden",
                      activeCategory === category.slug 
                        ? "max-h-[600px] opacity-100 visible" 
                        : "max-h-0 opacity-0 invisible"
                    )}
                    style={{ top: scrolled ? "80px" : "136px" }}
                  >
                    <div className="container-luxury py-16 grid grid-cols-12 gap-12">
                       <div className="col-span-3">
                          <span className="text-gold text-[10px] uppercase tracking-[0.4em] font-bold mb-6 block">The Collection</span>
                          <h3 className="font-display text-4xl text-charcoal italic mb-6 leading-tight">{category.name}</h3>
                          <p className="text-muted-foreground text-sm font-light leading-relaxed italic border-l-2 border-gold/10 pl-6 mb-8">
                             {category.description || "Discover our curated heritage pieces, where tradition meets contemporary elegance."}
                          </p>
                          <Link 
                            to={`/shop?category=${category.slug}`}
                            className="text-[10px] uppercase tracking-widest font-bold text-charcoal hover:text-gold transition-colors flex items-center gap-2"
                          >
                             Explore All <ArrowRight size={14} />
                          </Link>
                       </div>
                       
                       <div className="col-span-5 grid grid-cols-2 gap-x-12 gap-y-4 pt-12">
                          {category.subcategories.map((sub) => (
                            <Link
                              key={sub.slug}
                              to={`/shop?category=${category.slug}&subcategory=${sub.slug}`}
                              className="text-xs uppercase tracking-widest text-charcoal/60 hover:text-gold transition-all font-medium py-2 border-b border-gold/5 hover:translate-x-1"
                            >
                              {sub.name}
                            </Link>
                          ))}
                       </div>

                       <div className="col-span-4 relative aspect-[4/5] overflow-hidden group">
                          <img
                            src={getOptimizedImageUrl(category.image_url, { width: 500 })}
                            alt={category.name}
                            className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-charcoal/20 transition-opacity group-hover:opacity-0" />
                          <div className="absolute bottom-6 left-6 text-white">
                             <span className="text-[10px] uppercase tracking-[0.3em] font-bold">Editorial</span>
                             <p className="font-display text-xl italic uppercase font-bold tracking-widest mt-1">Heritage 2.0</p>
                          </div>
                       </div>
                    </div>
                  </div>
                </div>
              ))}
            </nav>
          </div>

          {/* Center: Prestige Logo */}
          <Link 
            to="/" 
            className="absolute left-1/2 -translate-x-1/2 transition-transform duration-700 hover:scale-105 group flex items-center gap-3"
          >
            <img 
              src="/logo.png" 
              alt="Leestone Logo" 
              className="h-10 md:h-12 w-auto"
              width={160}
              height={48}
              fetchPriority="high"
            />
            <h1 className={cn(
              "font-display text-sm xs:text-lg sm:text-2xl md:text-3xl tracking-[0.1em] xs:tracking-[0.15em] sm:tracking-[0.2em] uppercase transition-colors duration-700",
              scrolled ? "text-charcoal" : (isHeroPage ? "text-white" : "text-charcoal")
            )}>
              Leestone<span className="text-gold group-hover:animate-pulse">.</span>
            </h1>
          </Link>

          {/* Right: Actions */}
          <div className="flex items-center gap-2 sm:gap-6 flex-1 justify-end">
            <button
              onClick={() => setSearchOpen(true)}
              className={cn(
                "p-2 transition-all hover:text-gold hover:scale-110 duration-300",
                scrolled ? "text-charcoal" : (isHeroPage ? "text-white" : "text-charcoal")
              )}
              aria-label="Discovery Portal"
            >
              <Search size={20} strokeWidth={1.5} />
            </button>

            {/* Admin Access - Discreet & Premium */}
            {!adminLoading && user && isAdmin && (
              <Link
                to="/admin"
                className={cn(
                  "hidden lg:flex items-center gap-2 text-xs uppercase tracking-wider font-bold px-4 py-2 border transition-all duration-300",
                  scrolled 
                    ? "border-gold/20 text-charcoal hover:bg-ivory" 
                    : (isHeroPage ? "border-white/20 text-white hover:bg-white/10" : "border-gold/20 text-charcoal hover:bg-ivory")
                )}
              >
                <LayoutDashboard size={14} /> Admin
              </Link>
            )}

            <Link
              to={user ? "/profile" : "/auth"}
              className={cn(
                "hidden sm:flex p-2 transition-all hover:text-gold hover:scale-110 duration-300 items-center justify-center",
                scrolled ? "text-charcoal" : (isHeroPage ? "text-white" : "text-charcoal")
              )}
              aria-label="My Identity"
            >
              <User size={20} strokeWidth={1.5} />
            </Link>

            <CartDrawer className={scrolled ? "text-charcoal" : (isHeroPage ? "text-white" : "text-charcoal")} />
          </div>
        </div>
      </div>

      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
      
      <MobileMenu 
        isOpen={mobileMenuOpen} 
        onClose={() => setMobileMenuOpen(false)} 
        categories={availableCategories} 
        user={user} 
      />
    </header>
  );
}

// Separate component for Portal usage
import { createPortal } from "react-dom";

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  categories: any[];
  user: any;
}

function MobileMenu({ isOpen, onClose, categories, user }: MobileMenuProps) {
  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div 
          key="mobile-menu"
          initial={{ x: "-100%" }}
          animate={{ x: 0 }}
          exit={{ x: "-100%" }}
          transition={{ type: "spring", damping: 30, stiffness: 300, mass: 1 }}
          className="fixed inset-0 bg-white z-[9999] flex flex-col overflow-hidden shadow-2xl"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <div className="flex items-center justify-between p-8 border-b border-gold/5 bg-ivory/30 h-24 shrink-0">
             <h1 className="font-display text-4xl tracking-widest uppercase text-charcoal">LS<span className="text-gold">.</span></h1>
             <button onClick={onClose} className="w-12 h-12 flex items-center justify-center text-charcoal hover:text-gold transition-colors">
                <X size={32} strokeWidth={1} />
             </button>
          </div>
          
          <div className="flex-1 overflow-y-auto px-6 sm:px-10 py-6 sm:py-10 flex flex-col overscroll-contain">
             <span className="text-gold text-[10px] uppercase tracking-[0.5em] font-bold mb-6 sm:mb-8 block shrink-0">Navigation</span>
             <nav className="space-y-4 sm:space-y-6 shrink-0">
                {categories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/shop?category=${category.slug}`}
                    className="text-2xl sm:text-3xl md:text-4xl font-display text-charcoal hover:text-gold transition-all block italic hover:translate-x-2"
                    onClick={onClose}
                  >
                    {category.name}
                  </Link>
                ))}
             </nav>

             <div className="mt-auto pt-8 sm:pt-16 space-y-6 sm:space-y-8 shrink-0">
                <div className="grid grid-cols-2 gap-3 sm:gap-6">
                    <Link to={user ? "/profile" : "/auth"} onClick={onClose}>
                       <div className="flex flex-col gap-1 sm:gap-2 p-3 sm:p-5 bg-ivory/50 border border-gold/10 group active:scale-95 transition-transform">
                          <span className="text-[8px] sm:text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Client Identity</span>
                          <p className="font-display text-base sm:text-xl text-charcoal group-hover:text-gold">{user ? "My Profile" : "Sign In"}</p>
                       </div>
                    </Link>
                    <Link to="/contact" onClick={onClose}>
                       <div className="flex flex-col gap-1 sm:gap-2 p-3 sm:p-5 bg-ivory/50 border border-gold/10 group active:scale-95 transition-transform">
                          <span className="text-[8px] sm:text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Concierge</span>
                          <p className="font-display text-base sm:text-xl text-charcoal group-hover:text-gold">Speak With Us</p>
                       </div>
                    </Link>
                </div>
                <p className="text-center text-[10px] sm:text-xs uppercase tracking-widest text-muted-foreground font-medium pt-2 sm:pt-4 text-balance">Leestone Heritage · Established MCMLXXXV</p>
             </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}


